import { Button, colors, IconButton } from '@dev-spendesk/grapes';
import { useEffect, useRef, useState } from 'react';

export type TooltipProps = {
  title: string;
  content: string | React.ReactNode;
  step: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip: () => void;
  isLastStep?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  target?: { width: number; height: number; x: number; y: number };
  isVisible: boolean;
};

const getArrowContainerClassName = (placement: string) => {
  const baseClasses = 'absolute h-12 w-12 overflow-hidden';
  switch (placement) {
    case 'top':
      return `${baseClasses} -bottom-12 left-1/2 -translate-x-1/2`;
    case 'bottom':
      return `${baseClasses} -top-12 left-1/2 -translate-x-1/2`;
    case 'left':
      return `${baseClasses} -right-12 top-1/2 -translate-y-1/2`;
    default: // right
      return `${baseClasses} -left-12 top-1/2 -translate-y-1/2`;
  }
};

const getArrowClassName = (placement: string) => {
  const baseClasses = 'absolute h-8 w-8 bg-background-complementary-default rounded-[1px] rotate-45';
  switch (placement) {
    case 'top':
      return `${baseClasses} -top-4 left-1/2 -translate-x-1/2`;
    case 'bottom':
      return `${baseClasses} -bottom-4 left-1/2 -translate-x-1/2`;
    case 'left':
      return `${baseClasses} -left-4 top-1/2 -translate-y-1/2`;
    default: // right
      return `${baseClasses} -right-4 top-1/2 -translate-y-1/2`;
  }
};

export const Tooltip = ({
  title,
  content,
  step,
  onNext,
  onPrevious,
  onSkip,
  isVisible,
  isLastStep,
  placement = 'top',
  target = { width: 0, height: 0, x: 0, y: 0 },
}: TooltipProps) => {
  const tooltipReference = useRef<HTMLDivElement>(null);
  const [tooltipDimensions, setTooltipDimensions] = useState({
    width: 0,
    height: 0,
  });
  const gap = 16;

  useEffect(() => {
    if (tooltipReference.current) {
      setTooltipDimensions({
        width: tooltipReference.current.offsetWidth,
        height: tooltipReference.current.offsetHeight,
      });
    }
  }, [isVisible, content]);

  if (!isVisible) {
    return null;
  }

  const getPositionStyle = () => {
    switch (placement) {
      case 'top':
        return {
          left: target.x - (tooltipDimensions.width / 2 - target.width / 2),
          top: target.y - tooltipDimensions.height - gap,
        };
      case 'bottom':
        return {
          left: target.x - (tooltipDimensions.width / 2 - target.width / 2),
          top: target.y + target.height + gap,
        };
      case 'left':
        return {
          left: target.x - tooltipDimensions.width - gap,
          top: target.y - (tooltipDimensions.height / 2 - target.height / 2),
        };
      case 'right':
        return {
          left: target.x + target.width + gap,
          top: target.y - (tooltipDimensions.height / 2 - target.height / 2),
        };
      default:
        return {
          left: target.x - tooltipDimensions.width / 2,
          top: target.y - tooltipDimensions.height,
        };
    }
  };

  return (
    <div
      ref={tooltipReference}
      className="pointer-events-auto absolute z-50 w-[276px] rounded-12 bg-background-complementary-default p-16 text-content-complementary elevation-l"
      style={{
        ...getPositionStyle(),
        visibility: tooltipDimensions.height === 0 ? 'hidden' : 'visible', // Prevent flash of incorrect positioning
      }}
    >
      {/* // add triangle tip in the tooltip */}
      <div className={getArrowContainerClassName(placement)}>
        <div className={getArrowClassName(placement)} />
      </div>
      <div className="flex items-center justify-between mb-8">
        <h3 className="title-l">{title}</h3>
        <IconButton
          iconName="cross"
          className="shrink-0"
          iconColor={colors.backgroundPrimaryDefault}
          onClick={onSkip}
          aria-label="Close"
        />
      </div>
      <p className="mb-8 body-m">{content}</p>
      <div className="flex items-center justify-between">
        <div className="body-m">{step}/3</div>
        <div className="flex gap-8">
          {onPrevious && (
            <Button
              text="Back"
              onClick={onPrevious}
              variant="tertiaryBrand"
              className="text-content-complementary"
            />
          )}
          {onNext && (
            <Button text="Next" onClick={onNext} variant="secondaryNeutral" />
          )}
          {isLastStep && (
            <Button text="Finish" onClick={onSkip} variant="secondaryNeutral" />
          )}
        </div>
      </div>
    </div>
  );
};
